<script lang="ts">
  import Button from '../common/Button.svelte';
  import FormInput from '../common/FormInput.svelte';
  import MultiSelect from '../common/MultiSelect.svelte';
  import Tooltip from '../common/Tooltip.svelte';
  import Close from '../icons/Close.svelte';
  import Info from '../icons/Info.svelte';
  import { getBudgetableCategories } from '../../lib/constants.js';
  import { calculateAverageMonthlyIncome, formatCurrency } from '../../lib/analytics.js';
  import type { Transaction } from '../../lib/types.js';

  type Props = {
    isOpen: boolean;
    categories: string[];
    existingBudgets: Array<{ name: string; categories: string[]; type: 'monthly' | 'quarterly' | 'yearly' }>;
    transactions: Transaction[];
    monthlyIncomeTarget: number; // Pass from parent
    totalMonthlyBudgets: number; // Pass from parent
    onClose: () => void;
    onSave: (name: string, categories: string[], type: 'monthly' | 'quarterly' | 'yearly', limit: number, allowRollover: boolean, isFixedExpense: boolean, resetRolloverYearly: boolean) => Promise<void>;
  };

  let { isOpen, categories, existingBudgets, transactions, monthlyIncomeTarget, totalMonthlyBudgets, onClose, onSave }: Props = $props();

  let budgetName = $state('');
  let selectedCategories = $state<string[]>([]);
  let budgetType = $state<'monthly' | 'quarterly' | 'yearly'>('monthly');
  let limitValue = $state('');
  let allowRollover = $state(false);
  let resetRolloverYearly = $state(false);
  let isFixedExpense = $state(false);
  let isSubmitting = $state(false);
  let userEditedName = $state(false); // Track if user manually edited the name
  let userEditedLimit = $state(false); // Track if user manually edited the limit

  // Default resetRolloverYearly to true when allowRollover is enabled, reset when disabled
  $effect(() => {
    if (allowRollover) {
      resetRolloverYearly = true;
    } else {
      resetRolloverYearly = false;
    }
  });

  // Calculate average monthly income from transactions
  const avgMonthlyIncome = $derived(calculateAverageMonthlyIncome(transactions));

  // Calculate remaining income after accounting for current limit input
  const remainingAfterThisBudget = $derived.by(() => {
    const currentLimit = parseFloat(limitValue) || 0;
    return monthlyIncomeTarget - totalMonthlyBudgets - currentLimit;
  });

  // Get all categories that are already used in budgets of the selected type
  const usedCategories = $derived.by(() => {
    const used = new Set<string>();
    existingBudgets
      .filter(b => b.type === budgetType)
      .forEach(b => {
        b.categories.forEach(cat => used.add(cat));
      });
    return used;
  });

  // Filter out categories that are already used in other budgets of the selected type,
  // and remove non-budgetable categories (Uncategorized, Income, Transfer, CC Payment)
  const availableCategories = $derived(
    getBudgetableCategories(categories).filter(cat => !usedCategories.has(cat))
  );

  // Auto-fill budget name from first selected category if user hasn't manually edited it
  $effect(() => {
    // Only auto-fill if user hasn't manually edited the name
    if (!userEditedName && selectedCategories.length > 0) {
      budgetName = selectedCategories[0];
    }
  });

  // Initialize budget limit to 0 when modal opens
  $effect(() => {
    if (isOpen && !limitValue) {
      limitValue = '0';
    }
  });

  // Check if form is valid
  const isFormValid = $derived.by(() => {
    if (!budgetName.trim()) return false;
    if (selectedCategories.length === 0) return false;
    const limit = parseFloat(limitValue);
    if (isNaN(limit) || limit <= 0) return false;
    return true;
  });

  const handleSave = async () => {
    // Button is disabled if form is invalid, so we can proceed directly
    const limit = parseFloat(limitValue);

    isSubmitting = true;
    try {
      await onSave(budgetName.trim(), selectedCategories, budgetType, limit, allowRollover, isFixedExpense, resetRolloverYearly);
      handleClose();
    } catch (err) {
      console.error('Error saving budget:', err);
      alert(err instanceof Error ? err.message : 'Failed to save budget. Please try again.');
    } finally {
      isSubmitting = false;
    }
  };

  const handleClose = () => {
    budgetName = '';
    selectedCategories = [];
    budgetType = 'monthly';
    limitValue = '';
    allowRollover = false;
    resetRolloverYearly = false;
    isFixedExpense = false;
    userEditedName = false;
    userEditedLimit = false;
    onClose();
  };

  const handleNameInput = () => {
    userEditedName = true;
  };

  const handleLimitInput = () => {
    userEditedLimit = true;
  };

  const handleBackdropClick = (event: MouseEvent) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  const handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleClose();
    }
  };
</script>

<svelte:window onkeydown={handleEscape} />

{#if isOpen}
  <div class="modal-backdrop" onclick={handleBackdropClick}>
    <div class="modal">
      <div class="modal-header">
        <h2>Add New Budget</h2>
        <button class="close-btn" onclick={handleClose} aria-label="Close modal">
          <Close size={20} />
        </button>
      </div>

      <div class="modal-content">
        <div class="form-section">
          <div class="form-row">
            <FormInput
              label="Budget Name"
              bind:value={budgetName}
              type="text"
              placeholder="e.g., Family Health"
              required
              disabled={isSubmitting}
              oninput={handleNameInput}
            />

            <FormInput
              label="Budget Limit"
              bind:value={limitValue}
              type="number"
              placeholder="500"
              required
              disabled={isSubmitting}
              oninput={handleLimitInput}
            />
          </div>

          {#if budgetType === 'monthly' && limitValue && parseFloat(limitValue) > 0 && remainingAfterThisBudget < 0}
            <div class="validation-message">
              Exceeds available income by {formatCurrency(Math.abs(remainingAfterThisBudget))}
            </div>
          {/if}

          <div class="options-and-type-row">
            <div class="type-buttons-compact">
              <button
                type="button"
                class="type-btn-compact"
                class:active={budgetType === 'monthly'}
                onclick={() => budgetType = 'monthly'}
                disabled={isSubmitting}
              >
                Monthly
              </button>
              <button
                type="button"
                class="type-btn-compact"
                class:active={budgetType === 'quarterly'}
                onclick={() => budgetType = 'quarterly'}
                disabled={isSubmitting}
              >
                Quarterly
              </button>
              <button
                type="button"
                class="type-btn-compact"
                class:active={budgetType === 'yearly'}
                onclick={() => budgetType = 'yearly'}
                disabled={isSubmitting}
              >
                Yearly
              </button>
            </div>

            <div class="options-group">
              <label class="option-checkbox">
                <input
                  type="checkbox"
                  bind:checked={allowRollover}
                  disabled={isSubmitting}
                />
                <Tooltip text="Unused budget carries over to next period" delay={500}>
                  <span class="underlined">Allow Rollover</span>
                </Tooltip>
              </label>

              <label class="option-checkbox">
                <input
                  type="checkbox"
                  bind:checked={isFixedExpense}
                  disabled={isSubmitting}
                />
                <Tooltip text="Expected to be exact amount (mortgage, subscriptions, insurance)" delay={500}>
                  <span class="underlined">Fixed Expense</span>
                </Tooltip>
              </label>

              <label class="option-checkbox" class:invisible={!allowRollover}>
                <input
                  type="checkbox"
                  bind:checked={resetRolloverYearly}
                  disabled={isSubmitting || !allowRollover}
                />
                <Tooltip text="Rollover amount resets at the start of each year" delay={500}>
                  <span class="underlined">Reset Yearly</span>
                </Tooltip>
              </label>
            </div>
          </div>
        </div>

        <!-- Categories section -->
        {#if availableCategories.length === 0}
          <div class="form-group">
            <label>Categories</label>
            <p class="info-message">
              All categories already have a {budgetType} budget. Change budget type or manage existing budgets.
            </p>
          </div>
        {:else}
          <div class="form-group">
            <MultiSelect
              bind:value={selectedCategories}
              options={availableCategories}
              label="Categories"
              placeholder="Select categories..."
              disabled={isSubmitting}
              maxHeight="200px"
            />
          </div>
        {/if}

      </div>

      <div class="modal-footer">
        <Button variant="ghost" onclick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onclick={handleSave}
          disabled={isSubmitting || !isFormValid || availableCategories.length === 0}
        >
          {isSubmitting ? 'Saving...' : 'Add Budget'}
        </Button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: var(--z-modal);
    animation: fadeIn 0.15s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .modal {
    background: var(--color-background);
    border-radius: var(--radius-xl);
    width: 90%;
    max-width: 600px;
    max-height: 95vh;
    display: flex;
    flex-direction: column;
    box-shadow: var(--shadow-2xl);
    animation: slideUp var(--transition-fast) ease-out;
  }

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-2xl);
    border-bottom: 1px solid var(--color-border);
  }

  .modal-header h2 {
    margin: 0;
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
  }

  .close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: var(--spacing-sm);
    color: var(--color-text-tertiary);
    transition: color var(--transition-fast);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .close-btn:hover {
    color: var(--color-text-primary);
  }

  .modal-content {
    padding: var(--spacing-2xl);
    overflow-y: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
  }

  .form-row {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: var(--spacing-md);
  }

  .validation-message {
    padding: var(--spacing-sm) var(--spacing-md);
    background: var(--color-warning-light);
    color: var(--color-warning-darker);
    border-radius: var(--radius-md);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    border: 1px solid var(--color-warning);
  }

  .options-and-type-row {
    display: flex;
    align-items: center;
    gap: var(--spacing-lg);
    padding: var(--spacing-md);
    background: var(--color-background-secondary);
    border-radius: var(--radius-md);
  }

  .type-buttons-compact {
    display: flex;
    gap: var(--spacing-xs);
    background: var(--color-background);
    padding: var(--spacing-xs);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
    flex-shrink: 0;
  }

  .type-btn-compact {
    padding: var(--spacing-xs) var(--spacing-sm);
    background: transparent;
    border: none;
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: all var(--transition-fast);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-secondary);
  }

  .type-btn-compact:hover:not(:disabled) {
    background: var(--color-primary-light);
    color: var(--color-text-primary);
  }

  .type-btn-compact.active {
    background: var(--color-primary);
    color: white;
  }

  .type-btn-compact:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .options-group {
    display: flex;
    gap: var(--spacing-sm);
    flex: 1;
    min-width: 0;
  }

  .option-checkbox {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    cursor: pointer;
    user-select: none;
    position: relative;
    flex-shrink: 0;
  }

  .option-checkbox.invisible {
    visibility: hidden;
    pointer-events: none;
  }

  .option-checkbox input[type="checkbox"] {
    cursor: pointer;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .option-checkbox span {
    font-size: var(--font-size-sm);
    color: var(--color-text-primary);
    white-space: nowrap;
  }

  .option-checkbox span.underlined {
    text-decoration: underline;
    text-decoration-style: dotted;
    text-decoration-color: var(--color-text-tertiary);
    text-underline-offset: 2px;
    cursor: help;
  }

  .option-checkbox:hover span.underlined {
    color: var(--color-primary);
    text-decoration-color: var(--color-primary);
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .form-group > label {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-secondary);
  }

  .info-message {
    font-size: var(--font-size-sm);
    color: var(--color-text-tertiary);
    margin: 0;
    padding: var(--spacing-md);
    background: var(--color-background-secondary);
    border-radius: var(--radius-md);
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-md);
    padding: var(--spacing-2xl);
    border-top: 1px solid var(--color-border);
  }
</style>
